//internal-deps
import { isEmpty } from "@swiggy-private/core-ui";

//constants
import {
    ADD_NEW_CARD_PROMO_BANNER_LINK,
    LP_PROMO_BANNER_LINK,
    INAPP_UPI_BANNER_LINK,
    PAYMENT_METHOD_DOWNTIME_BANNER_LINK,
    PPM_FOODCARD_PROMO_BANNER_LINK,
    REGULAR_FOODCARD_PROMO_BANNER_LINK,
    IM_NCM_BANNER_LINK,
} from "@payment-client/constants/TextDict";

//defs
import { GetPaymentMethodsResponse } from "@payment-client/interfaces/PaymentApi";
import { BannerParams } from "@payment-client/interfaces/Banner";

//helpers
import { hasPaymentMethods } from "./PaymentHelper";

interface IsPaymentBannerVisibleParameters {
    paymentGroups: GetPaymentMethodsResponse["paymentGroup"];
    couponApplicablePaymentGroups: GetPaymentMethodsResponse["paymentGroup"];
    couponApplied: boolean;
    showCouponGroupsOnly: boolean;
    banner: GetPaymentMethodsResponse["banner"];
}

export const isInAppUPIBanner = (banner: GetPaymentMethodsResponse["banner"]): boolean =>
    banner?.cta?.link === INAPP_UPI_BANNER_LINK;

export const isAddNewCardPromoBanner = (banner: GetPaymentMethodsResponse["banner"]): boolean =>
    banner?.cta?.link === ADD_NEW_CARD_PROMO_BANNER_LINK;

export const isLazypayPromoBanner = (banner: GetPaymentMethodsResponse["banner"]): boolean =>
    banner?.cta?.link === LP_PROMO_BANNER_LINK;

export const isPpmFoodcardPromoBanner = (banner: GetPaymentMethodsResponse["banner"]): boolean =>
    banner?.cta?.link === PPM_FOODCARD_PROMO_BANNER_LINK;

export const isRegularFoodcardPromoBanner = (banner: GetPaymentMethodsResponse["banner"]): boolean =>
    banner?.cta?.link === REGULAR_FOODCARD_PROMO_BANNER_LINK;

export const isPaymentMethodDowntimeBanner = (banner: GetPaymentMethodsResponse["banner"]): boolean =>
    banner?.cta?.link === PAYMENT_METHOD_DOWNTIME_BANNER_LINK;

export const isEmiBanner = (banner: GetPaymentMethodsResponse["banner"]): boolean =>
    banner?.cta?.link === IM_NCM_BANNER_LINK;

const hasPaymentGroupsAndPaymentMethods = (paymentGroups: GetPaymentMethodsResponse["paymentGroup"]): boolean =>
    paymentGroups.length > 0 && hasPaymentMethods(paymentGroups);

const checkIfPlatformLevelCouponApplied = (
    paymentGroups: GetPaymentMethodsResponse["paymentGroup"],
    couponApplicablePaymentGroups: GetPaymentMethodsResponse["paymentGroup"],
): boolean =>
    !hasPaymentGroupsAndPaymentMethods(paymentGroups) &&
    hasPaymentGroupsAndPaymentMethods(couponApplicablePaymentGroups);

/*
1. If no coupon is applied,  banner sent by backend will be shown
2. If platform coupon(SWIGGYIT, JUMBO, PARTY etc) is applied, then only VSC banner will be shown given VSC banner data exists in payments response. Other banners like KYC/SwiggyMoney won't be shown in this case even banner data exists in payments response
3. If payments specific coupon(ONECARD50, PAYTM30 etc) is applied,
    a) VSC banner won't be shown even on non coupon payments page (2nd page)
    b) no banner will be show on coupon payments page (1st page)
    c) KYC banner/SM banner will be shown if exists on non coupon payments page
*/
export const canShowPaymentBanner = ({
    paymentGroups,
    couponApplicablePaymentGroups,
    couponApplied,
    showCouponGroupsOnly,
    banner,
}: IsPaymentBannerVisibleParameters): boolean => {
    if (isEmpty(banner)) {
        return false;
    }

    const isPlatformLevelCouponApplied = checkIfPlatformLevelCouponApplied(
        paymentGroups,
        couponApplicablePaymentGroups,
    );

    if (!couponApplied) {
        return true;
    }

    if (isPlatformLevelCouponApplied) {
        if (
            isAddNewCardPromoBanner(banner) ||
            isLazypayPromoBanner(banner) ||
            isPpmFoodcardPromoBanner(banner) ||
            isRegularFoodcardPromoBanner(banner)
        ) {
            return true;
        }

        return false;
    }

    return !showCouponGroupsOnly;
};

export const getBannerDimensions = (
    banner: GetPaymentMethodsResponse["banner"],
): { width: number; height: number } | undefined => {
    const toUseAspectRatio =
        banner?.cta?.link &&
        [REGULAR_FOODCARD_PROMO_BANNER_LINK, PPM_FOODCARD_PROMO_BANNER_LINK].includes(banner.cta.link);
    if (toUseAspectRatio && banner?.aspectRatio) {
        const width = window.innerWidth - 32;
        const height = width / banner.aspectRatio;
        return {
            width,
            height,
        };
    }
};

export const getBannerParams = (bannerData: GetPaymentMethodsResponse["banner"]): BannerParams => {
    return {
        /* eslint-disable @typescript-eslint/naming-convention */
        entity_counters: [
            {
                entity_id: bannerData?.bannerId || "",
                entity_type: "BANNER",
                count: Number(bannerData?.frequencyCount ?? 0) + 1,
            },
        ],
    };
};
